import { FaqItem } from 'components/AiPage/FaqAiPage'
import {
  Intelligent,
  Predictive,
  AIPowered,
  Conversational,
  Automated,
  Workforce,
  RetailIcon,
  TravelIcon,
  EducationIcon,
  FMCGIcon,
  B2BIcon,
  FinanceIcon,
  EdtechIcon,
} from 'components/AiInRecruitingPage/Icons'
import type {
  AiInRecruitingBannerData,
  AiInRecruitingTransformSectionData,
  AiInRecruitingWhySectionData,
  AiInRecruitingCapabilitiesData,
  AiInRecruitingCompareSectionData,
  AiInRecruitingUseCasesSectionData,
  AiInRecruitingIndustrySectionData,
  AiInRecruitingImpactSectionData,
  AiInRecruitingImplementationSectionData,
  AiInRecruitingIntegrationSectionData,
  AiInRecruitingChallengesSectionData,
  AiInRecruitingWhyChooseSectionData,
  AiInRecruitingFutureSectionData,
} from './types'

export const faqData: FaqItem[] = [
  {
    question: 'What is AI recruiting?',
    answer:
      'AI recruiting leverages machine learning and natural language processing to process candidate information, align candidate profiles to job descriptions, and score candidates on job fit. It automates the process of resume screening with a systematic assessment.',
  },
  {
    question: 'How does AI recruiting work?',
    answer:
      'AI recruiting tools transform resumes and job descriptions into data, which is scored using models that assess skills, experience patterns and fit for the role. These models improve over time using hiring outcomes and recruiter feedback.',
  },
  {
    question: "What's the difference between AI recruiting and an ATS?",
    answer:
      'ATS stores and manages candidate data, while AI recruiting systems score and rank candidates. AI recruiting systems enhance ATSs by providing semantic matching, predictive scoring and decision support.',
  },
  {
    question: ' Does AI recruiting speed up hiring?',
    answer:
      'Yes. AI speeds up hiring by automating resume review, shortlisting and scheduling interviews. The biggest impact is in the initial filtering stage, where the manual process is replaced with immediate ranking.',
  },
  {
    question: 'Does AI recruiting improve hiring quality?',
    answer:
      'AI enhances hiring quality by consistently evaluating candidates. It eliminates bias in the screening process and ranks candidates based on objective measures of fit, including skills, experience and job similarity.',
  },
  {
    question: 'What are the typical applications of AI in hiring?',
    answer:
      'Use cases include screening resumes, matching candidates, predictive hiring, chatbots, scheduling interviews, and forecasting hiring needs. These aim to increase efficiency, effectiveness and volume.',
  },
  {
    question: 'Can AI recruiting be used for high volume hiring?',
    answer:
      'Yes. AI recruiting is ideal for high-volume hiring as it can quickly review and assess large applicant pools, without adding to recruiter workload.',
  },
  {
    question: 'What data is required for AI recruiting?',
    answer:
      'AI recruiting needs resumes, job descriptions, and historical data about hiring decisions, such as whether candidates were hired. This results in improved candidate scoring and matching.',
  },
  {
    question: 'Can AI recruiting reduce bias in hiring?',
    answer:
      'AI can help reduce bias by creating a consistent scoring system, but it&apos;s dependent on the system and data. Effective systems include bias checks, fairness controls, and regular audits to ensure compliance.',
  },
  {
    question: 'How long does it take to implement AI recruiting?',
    answer:
      'AI recruiting systems typically take 6-12 weeks to implement, depending on data preparation, integration with existing systems, and automation levels.',
  },
]

export const aiInRecruitingBannerData: AiInRecruitingBannerData = {
  title: 'AI in',
  titleHighlight: 'Recruiting',
  subtitle:
    'Intelligent Talent Acquisition for Faster, Smarter Hiring Outcomes',
  image: '/assets/images/ai-in-recruiting-banner.png',
  imageAlt: 'AI in Recruiting',
  paragraphs: [
    'AI in recruiting leverages machine learning, natural language processing, and predictive analytics to revolutionise talent acquisition, assessment, and hiring. Rather than manual screening and guesswork, talent acquisition teams can now find the right fit more quickly, increase the quality of hire, and save time and money with data-driven accuracy.',
    'Traditional hiring processes struggle with high applicant volumes, delayed screening, inconsistent evaluation, and unconscious bias. This results in delayed shortlisting, higher candidate drop-off rates, and increased dependency on recruiter bandwidth instead of data-driven prioritization. AI overcomes these challenges by enabling structured candidate scoring frameworks, automated semantic matching of job descriptions and candidate profiles, and adaptive learning systems to enhance hiring outcomes.',
    'At WebClues Infotech, we enable organizations to transform their hiring ecosystem through AI-powered solutions. We develop solutions such as smart resume matching, predictive candidate scoring, and chatbots for hiring that help hiring teams accelerate time-to-hire, enhance hiring success rates, and streamline recruitment processes.',
  ],
}

export const aiCreditScoringCapabilitiesData: AiInRecruitingCapabilitiesData = {
  title: 'Why Your Hiring Process Is Slowing Growth Without AI in Recruiting',
  subtitle:
    'Recruitment teams today operate in a high-pressure environment where speed and accuracy directly impact business performance. Traditional hiring workflows often fail to keep up.',
  items: [
    {
      id: '01',
      title: 'High Time-to-Hire',
      description:
        'Manual screening and slow interview coordination delay critical hiring decisions.',
    },
    {
      id: '02',
      title: 'Resume Overload',
      description:
        'Recruiters spend excessive time filtering irrelevant or unqualified applications.',
    },
    {
      id: '03',
      title: 'Inconsistent Candidate Evaluation',
      description:
        'Human-led screening often leads to subjective and non-standardized decisions.',
    },
    {
      id: '04',
      title: 'Talent Drop-Off',
      description:
        'Slow communication and delayed feedback cause top candidates to disengage.',
    },
    {
      id: '05',
      title: 'Hidden Hiring Bias',
      description:
        'Unstructured evaluation processes increase the risk of unconscious bias.',
    },
    {
      id: '06',
      title: 'Poor Hiring Visibility',
      description:
        'Lack of data-driven insights makes it difficult to optimize hiring performance.',
    },
  ],
}

export const aiInRecruitingTransformSectionData: AiInRecruitingTransformSectionData =
  {
    title: 'How AI in Recruiting Fixes What Traditional Hiring Cannot',
    subtitle:
      'AI transforms recruitment from a reactive, manual process into a predictive and intelligence-led system.',
    items: [
      {
        title: 'Smarter Candidate Matching',
        description:
          'Identify candidates who align with skills, experience, and role intent more accurately.',
      },
      {
        title: 'Faster Screening Cycles',
        description:
          'Automate resume parsing and shortlisting to reduce hiring delays.',
      },
      {
        title: 'Improved Candidate Experience',
        description:
          'Engage applicants instantly through AI assistants and automated updates.',
      },
      {
        title: 'Predictive Hiring Decisions',
        description:
          'Forecast candidate success and retention using behavioral and historical data.',
      },
      {
        title: 'Reduced Recruitment Costs',
        description:
          'Minimize manual effort and optimize hiring resources efficiently.',
      },
      {
        title: 'Scalable Hiring Operations',
        description:
          'Handle high-volume recruitment without increasing recruiter workload.',
      },
    ],
  }

export const aiInRecruitingWhySectionData: AiInRecruitingWhySectionData = {
  title:
    'AI Recruiting Capabilities That Transform Hiring Efficiency and Accuracy',
  subtitle:
    'WebClues Infotech helps companies implement AI-powered hiring systems to improve every step of the hiring process.',
  items: [
    {
      title: 'Intelligent Resume Parsing',
      description:
        'Automate the identification and assessment of skills, experience and fit.',
      icon: <Intelligent />,
    },
    {
      title: 'Predictive Candidate Scoring',
      description:
        'Prioritise candidates based on job fit, performance and retention.',
      icon: <Predictive />,
    },
    {
      title: 'AI-Powered Candidate Matching',
      description:
        'Semantically match job descriptions with internal and external candidates.',
      icon: <AIPowered />,
    },
    {
      title: 'Conversational Recruitment Assistants',
      description:
        'Manage candidate interactions, questions, scheduling and screening.',
      icon: <Conversational />,
    },
    {
      title: 'Automated Interview Scheduling',
      description: 'Eliminate scheduling bottlenecks with smart calendaring.',
      icon: <Automated />,
    },
    {
      title: 'Workforce Analytics & Hiring Insights',
      description:
        'Monitor hiring funnel metrics and refine hiring strategies.',
      icon: <Workforce />,
    },
  ],
}

export const aiCreditScoringCompareSectionData: AiInRecruitingCompareSectionData =
  {
    title: 'Traditional Hiring vs AI Recruiting Systems: See the Difference',
    subtitle:
      'Traditional recruitment depends on manual screening and fragmented decision-making, while AI-driven recruiting introduces automation, intelligence, and real-time insights.',
    headers: {
      capability: 'Feature',
      traditional: 'Traditional Hiring',
      aiPowered: 'AI Recruiting Systems',
    },
    rows: [
      {
        id: 1,
        capability: 'Candidate Screening',
        traditional: 'Manual resume review',
        aiPowered: 'Automated parsing and ranking',
      },
      {
        id: 2,
        capability: 'Hiring Speed',
        traditional: 'Slow and process-heavy',
        aiPowered: 'Fast, automated workflows',
      },
      {
        id: 3,
        capability: 'Candidate Matching',
        traditional: 'Human judgment-based',
        aiPowered: 'AI-driven semantic matching',
      },
      {
        id: 4,
        capability: 'Decision Making',
        traditional: 'Subjective evaluation',
        aiPowered: 'Data-backed predictive scoring',
      },
      {
        id: 5,
        capability: 'Candidate Engagement',
        traditional: 'Delayed communication',
        aiPowered: 'Real-time AI interaction',
      },
      {
        id: 6,
        capability: 'Bias Control',
        traditional: 'High variability',
        aiPowered: 'Structured and consistent evaluation',
      },
      {
        id: 7,
        capability: 'Hiring Insights',
        traditional: 'Limited reporting',
        aiPowered: 'Predictive analytics and dashboards',
      },
      {
        id: 8,
        capability: 'Scalability',
        traditional: 'Resource-dependent',
        aiPowered: 'Scalable across high volumes',
      },
    ],
  }

export const aiInRecruitingUseCasesSectionData: AiInRecruitingUseCasesSectionData =
  {
    title: 'High-Impact AI Recruiting Use Cases Across the Hiring Funnel',
    subtitle:
      'AI delivers measurable improvements when applied across the recruitment lifecycle.',
    items: [
      {
        title: 'Predictive Talent Sourcing:',
        description:
          'Identify high-potential candidates before they actively apply.',
      },
      {
        title: 'Automated Resume Screening:',
        description:
          'Reduce manual effort by filtering and ranking candidates instantly.',
      },
      {
        title: 'AI-Based Candidate Engagement:',
        description:
          'Improve response rates with real-time chatbots and assistants.',
      },
      {
        title: 'Intelligent Interview Shortlisting:',
        description: 'Select candidates based on data-driven fit scoring.',
      },
      {
        title: 'Attrition Risk Prediction:',
        description: 'Identify candidates with higher retention probability.',
      },
      {
        title: 'Workforce Demand Forecasting:',
        description:
          'Predict future hiring needs based on business growth patterns.',
      },
    ],
  }

export const aiInRecruitingIndustrySectionData: AiInRecruitingIndustrySectionData =
  {
    title: 'Industry-Specific AI Recruiting Solutions for Complex Environments',
    subtitle:
      'WebClues Infotech designs AI recruitment systems tailored to specific industry requirements and hiring complexities.',
    items: [
      {
        title: 'IT & Technology',
        description:
          'Technical skill matching, coding assessment automation, and predictive hiring.',
        icon: <RetailIcon />,
      },
      {
        title: 'BPO & Customer Support',
        description:
          'High-volume screening, chatbot-driven interviews, and rapid onboarding support.',
        icon: <B2BIcon />,
      },
      {
        title: 'Healthcare & Life Sciences',
        description:
          'Credential validation, compliance screening, and role-specific candidate matching.',
        icon: <FinanceIcon />,
      },
      {
        title: 'Banking & Financial Services',
        description:
          'Risk-aware hiring, compliance-driven screening, and structured evaluation models.',
        icon: <TravelIcon />,
      },
      {
        title: 'Manufacturing & Logistics',
        description:
          'Shift-based workforce optimization and bulk hiring automation.',
        icon: <EducationIcon />,
      },
      {
        title: 'Retail & E-Commerce',
        description:
          'Seasonal hiring automation and fast candidate filtering systems.',
        icon: <FMCGIcon />,
      },
      {
        title: 'Education & EdTech',
        description:
          'Enrollment predictions, personalized learning recommendations, and engagement automation.',
        icon: <EdtechIcon />,
      },
    ],
  }

export const aiInRecruitingImpactSectionData: AiInRecruitingImpactSectionData =
  {
    title: 'What Businesses Gain When They Implement AI in Recruiting',
    subtitle: 'AI transforms recruitment into a measurable growth function.',
    items: [
      '60% faster hiring cycles through automation',
      '3x improvement in candidate shortlisting accuracy',
      '50% reduction in recruitment operational effort',
      'Higher quality-of-hire through predictive scoring',
      'Improved candidate experience with real-time engagement',
      'Better hiring visibility through unified analytics',
    ],
  }

export const aiInRecruitingImplementationSectionData: AiInRecruitingImplementationSectionData =
  {
    title:
      'How We Implement AI Recruiting Solutions From Strategy to Deployment',
    subtitle: 'From strategy to deployment, built for faster, smarter hiring.',
    items: [
      {
        title: 'Recruitment Process Audit:',
        description:
          'Examine current hiring processes, bottlenecks and data architecture.',
      },

      {
        title: 'AI Use Case Definition:',
        description:
          'Determine the most valuable areas such as screening, sourcing or engagement.',
      },

      {
        title: 'Data Integration:',
        description: 'Integrate HRMS, ATS and candidate data.',
      },

      {
        title: 'Model Development:',
        description: 'Develop AI models for scoring, matching and prediction.',
      },
      {
        title: 'Testing & Validation:',
        description: 'Conduct controlled testing and fine-tune models.',
      },
      {
        title: 'Deployment & Scaling:',
        description: 'Seamlessly integrate AI into hiring processes. ',
      },
      {
        title: 'Continuous Optimization:',
        description:
          'Track results and improve models for improved hiring decisions.',
      },
    ],
  }

export const aiInRecruitingIntegrationSectionData: AiInRecruitingIntegrationSectionData =
  {
    title: 'AI Recruiting That Works With Your Existing HR Tech Stack',
    subtitle:
      'Seamless integration with your current systems ensures zero disruption.',
    items: [
      {
        label: 'Applicant Tracking Systems (ATS):',
        tools: 'Workday, Greenhouse, Lever, Taleo, Zoho Recruit',
      },
      {
        label: 'Analytics & Data Platforms:',
        tools: 'Power BI, Tableau, Snowflake, Google BigQuery',
      },
      {
        label: 'HR Platforms:',
        tools: 'SAP SuccessFactors, Oracle HCM, BambooHR',
      },
      {
        label: 'Assessment Tools:',
        tools: 'Codility, HackerRank, TestGorilla',
      },
      {
        label: 'Communication Tools:',
        tools: 'Slack, Microsoft Teams, Email automation systems',
      },
    ],
  }
export const aiInRecruitingChallengesSectionData: AiInRecruitingChallengesSectionData =
  {
    titleBlack: 'Common AI Recruiting Challenges and How to Overcome Them',
    subtitle:
      'Key challenges in AI recruiting and practical ways to solve them.',
    headerChallenge: 'AI Recruiting Challenges',
    headerSolution: 'WebClues Solutions',
    rows: [
      {
        challenge: 'Data fragmentation',
        solution: 'Unified ATS + HR data integration',
      },
      {
        challenge: 'Bias in hiring models',
        solution: 'Fairness checks and model audits',
      },
      {
        challenge: 'Integration complexity',
        solution: 'API-first architecture with ATS compatibility',
      },
      {
        challenge: 'Low recruiter adoption',
        solution: 'Training and guided AI workflows',
      },
      {
        challenge: 'Compliance risks',
        solution: 'GDPR/CCPA-aligned governance layer',
      },
      {
        challenge: 'ROI uncertainty',
        solution: 'Phased rollout with measurable KPIs',
      },
    ],
  }

export const aiInRecruitingWhyChooseSectionData: AiInRecruitingWhyChooseSectionData =
  {
    title: 'Why Smart Teams Trust WebClues for AI Recruiting',
    subtitle:
      'WebClues Infotech leverages AI engineering and a strong understanding of the hiring process to improve hiring outcomes.',
    items: [
      { iconKey: 'Scoring', title: 'AI-first recruitment architecture' },
      {
        iconKey: 'Automated',
        title: 'Custom-built hiring intelligence systems',
      },
      {
        iconKey: 'Predictive',
        title: 'Integration with existing HR ecosystems',
      },
      { iconKey: 'Alternative', title: 'Emphasis on hiring KPIs and ROI' },
      { iconKey: 'Fraud', title: 'Secure, compliant and scalable deployment' },
      {
        iconKey: 'Explainable',
        title: 'Dedicated implementation and support teams',
      },
    ],
  }

export const aiInRecruitingFutureSectionData: AiInRecruitingFutureSectionData =
  {
    title: 'The Next Wave of AI in Recruiting and What Comes Next',
    subtitle:
      'AI recruiting is rapidly moving from automation to intelligence-driven hiring.',
    trendsLabel: 'Key trends shaping the future of AI recruiting:',
    items: [
      {
        label: 'Generative AI Job Descriptions & Candidate Summaries:',
        description:
          'Generates job and candidate content, insights, and communications at scale.',
      },
      {
        label: 'Self-Operating Screening & Scheduling Tools:',
        description:
          'AI-powered processes to automate candidate shortlisting and interview scheduling.',
      },
      {
        label: 'Dynamic Predictive Hiring Analytics:',
        description:
          'Leverages real-time data to predict hiring outcomes, needs and fit.',
      },
      {
        label: 'AI-Powered Internal Talent Mapping & Mobility:',
        description: 'Uncovers internal talent gaps and career paths.',
      },
      {
        label: 'Bias Mitigation & Explanation Systems:',
        description: 'Provides fair and explainable hiring with auditable AI.',
      },
      {
        label: 'Hiring for Skills, not CVs:',
        description: 'Focuses on skills and abilities rather than CV keywords.',
      },
      {
        label: 'Learning Hiring Models:',
        description:
          'Systems that adapt and improve predictions based on hiring results and feedback.',
      },
    ],
  }
